/**
 * Keeps pdf.js's stylesheet (pdfjs-dist/web/pdf_viewer.css) inside the report
 * viewer.
 *
 * That stylesheet is written for pdf.js's whole viewer app, so it styles plain
 * global class names: `.sidebar` (its comments sidebar), `.dialog`,
 * `.popupMenu`, `.toggle-button` and more. Our left navigation is
 * `<aside class="sidebar">`, so the first time a report was opened the
 * stylesheet loaded and turned the nav white (white background, 8px corners, a
 * fixed 239px width) for the rest of the visit.
 *
 * vite.config.ts runs this PostCSS plugin on that one file: every selector is
 * put under PDFJS_SCOPE_CLASS, the class on the viewer's scroll container,
 * which holds everything pdf.js renders. Left global: `:root` (pdf.js's own
 * custom properties) and the hidden helpers pdf.js appends to <body>.
 */
import type { AtRule, Plugin, Rule } from 'postcss';

/** The class on ReportPdfDialog's pages container; pdf.js's CSS applies only inside it. */
export const PDFJS_SCOPE_CLASS = 'pdfjs-scope';

/** Elements pdf.js appends to <body> that its stylesheet hides. */
const BODY_HELPERS = ['.hiddenCanvasElement', '#hiddenCopyElement'];

/** The stylesheet the plugin rewrites. */
export function isPdfViewerCss(file: string | undefined): boolean {
  return !!file && /[\\/]pdfjs-dist[\\/]web[\\/]pdf_viewer\.css$/.test(file);
}

/** One selector, scoped to the viewer. */
export function scopePdfSelector(selector: string, scope = `.${PDFJS_SCOPE_CLASS}`): string {
  const s = selector.trim();
  if (s === ':root' || BODY_HELPERS.includes(s)) return s;
  // `:root *` (the fallback for browsers without light-dark()): the viewer's elements.
  if (s.startsWith(':root ')) return `${scope} ${s.slice(':root '.length).trim()}`;
  return `${scope} ${s}`;
}

/** A keyframe (`from`, `50%`) rather than a selector: its parent is the @keyframes rule. */
function isKeyframe(rule: Rule): boolean {
  const parent = rule.parent;
  return parent?.type === 'atrule' && /keyframes$/i.test((parent as AtRule).name);
}

/** PostCSS plugin: scopes pdf_viewer.css (and only it) to PDFJS_SCOPE_CLASS. */
export function scopePdfViewerCss(): Plugin {
  return {
    postcssPlugin: 'dietech-scope-pdfjs-viewer-css',
    Once(root) {
      if (!isPdfViewerCss(root.source?.input.file)) return;
      root.walkRules((rule) => {
        if (isKeyframe(rule)) return;
        rule.selectors = rule.selectors.map((sel) => scopePdfSelector(sel));
      });
    },
  };
}
